import type { MFARegister } from '../../components/pages/userAccount/hsbc/steps/defaultRegistration/step_3/passwordSetupForm';
import type { ZustandStore } from '../store.type';
import type { NamedSet } from 'zustand/middleware';

type RegistrationState = {
	registration: {
		firstName: string;
		lastName: string;
		companyName: string;
		isFormLoading: boolean;
		mfaData: Partial<MFARegister> | null;
		showTwoFactorAuth: boolean;
		hasUserSkipMfa: boolean;
	};
};

type RegistrationActions = {
	setRegistrationFirstName: (firstName: RegistrationState['registration']['firstName']) => void;
	setRegistrationLastName: (lastName: RegistrationState['registration']['lastName']) => void;
	setRegistrationCompanyName: (companyName: RegistrationState['registration']['companyName']) => void;
	setRegistrationFormLoading: (isFormLoading: RegistrationState['registration']['isFormLoading']) => void;
	setMfaData: (data: RegistrationState['registration']['mfaData']) => void;
	setShowTwoFactorAuth: (showTwoFactorAuth: RegistrationState['registration']['showTwoFactorAuth']) => void;
	setHasUserSkipMfa: (didUserSkipMfa: RegistrationState['registration']['hasUserSkipMfa']) => void;
	resetRegistrationForm: () => void;
};

export type RegistrationSlice = RegistrationState & RegistrationActions;

const initialState = {
	firstName: '',
	lastName: '',
	companyName: '',
	isFormLoading: false,
	mfaData: null,
	showTwoFactorAuth: false,
	hasUserSkipMfa: false,
};

const registrationInitialState: RegistrationState = {
	registration: initialState,
};

export function registrationSlice(set: NamedSet<ZustandStore>): RegistrationSlice {
	return {
		...registrationInitialState,
		setRegistrationFirstName: (firstName: RegistrationState['registration']['firstName']) => {
			set(
				(state: RegistrationState) => {
					state.registration.firstName = firstName;
					return state;
				},
				false,
				'[setRegistrationFirstName]',
			);
		},
		setRegistrationLastName: (lastName: RegistrationState['registration']['lastName']) => {
			set(
				(state: RegistrationState) => {
					state.registration.lastName = lastName;
					return state;
				},
				false,
				'[setRegistrationLastName]',
			);
		},
		setRegistrationCompanyName: (companyName: RegistrationState['registration']['companyName']) => {
			set(
				(state: RegistrationState) => {
					state.registration.companyName = companyName;
					return state;
				},
				false,
				'[setCompanyName]',
			);
		},
		setRegistrationFormLoading: (isFormLoading: RegistrationState['registration']['isFormLoading']) => {
			set(
				(state: RegistrationState) => {
					state.registration.isFormLoading = isFormLoading;
					return state;
				},
				false,
				'[setRegistrationFormLoading]',
			);
		},
		setMfaData(data: RegistrationState['registration']['mfaData']): void {
			set(
				(state: RegistrationState) => {
					state.registration.mfaData = { ...state.registration.mfaData, ...data };
					return state;
				},
				false,
				'[setMfaData]',
			);
		},
		setShowTwoFactorAuth: (showTwoFactorAuth: RegistrationState['registration']['showTwoFactorAuth']) => {
			set(
				(state: RegistrationState) => {
					state.registration.showTwoFactorAuth = showTwoFactorAuth;
					return state;
				},
				false,
				'[setShowTwoFactorAuth]',
			);
		},
		setHasUserSkipMfa: (didUserSkipMfa: RegistrationState['registration']['hasUserSkipMfa']) => {
			set(
				(state: RegistrationState) => {
					state.registration.hasUserSkipMfa = didUserSkipMfa;
					return state;
				},
				false,
				'[setHasUserSkipMfa]',
			);
		},
		resetRegistrationForm: () => {
			set(
				(state: RegistrationState) => {
					state.registration = initialState;
					return state;
				},
				false,
				'[resetRegistrationForm]',
			);
		},
	};
}
